import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Plus, Trash2, Loader2, Save, ArrowLeft, Wrench, Package, BookOpen } from "lucide-react";
import api, { money } from "@/lib/api";
import localApi from "@/lib/localApi";
import Layout from "@/components/Layout";
import { STATUSES } from "@/components/StatusBadge";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

let _itemSeq = 0;
const uid = () => `li_${Date.now()}_${_itemSeq++}`;
const emptyItem = (kind = "part") => ({ _key: uid(), description: "", quantity: 1, unit_price: 0, kind });

export default function InvoiceEditor() {
  const { id } = useParams();
  const editing = id && id !== "new";
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const [customers, setCustomers] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(editing);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(() => {
    const prefill = location.state?.prefill;
    return {
      doc_type: prefill?.doc_type || "invoice",
      customer_name: prefill?.customer_name || "", customer_phone: prefill?.customer_phone || "",
      customer_email: prefill?.customer_email || "",
      customer_business_name: "", customer_business_address: "",
      vehicle_make: prefill?.vehicle_make || prefill?.vehicle || "",
      vehicle_model: prefill?.vehicle_model || "", vehicle_year: prefill?.vehicle_year || "", vehicle_vin: prefill?.vehicle_vin || "",
      tax_rate: 0, notes: prefill?.notes || "", status: "pending",
      items: prefill?.items?.length
        ? prefill.items.map((it) => ({ _key: uid(), description: it.description || "", quantity: it.quantity ?? 1, unit_price: it.unit_price ?? 0, kind: it.kind || "part" }))
        : [emptyItem("part")],
    };
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    api.get("/customers").then(({ data }) => setCustomers(data || [])).catch(() => {});
    api.get("/catalog").then(({ data }) => setCatalog([...(data.custom || []), ...(data.builtin || [])])).catch(() => {});
    if (editing) {
      api.get(`/invoices/${id}`).then(({ data }) => {
        setForm({
          doc_type: data.doc_type || "invoice",
          customer_name: data.customer_name || "", customer_phone: data.customer_phone || "",
          customer_email: data.customer_email || "", customer_business_name: data.customer_business_name || "",
          customer_business_address: data.customer_business_address || "",
          vehicle_make: data.vehicle_make || "", vehicle_model: data.vehicle_model || "",
          vehicle_year: data.vehicle_year || "", vehicle_vin: data.vehicle_vin || "",
          tax_rate: data.tax_rate || 0, notes: data.notes || "", status: data.status || "pending",
          items: (data.items && data.items.length ? data.items : [emptyItem()]).map((it) => ({
            _key: uid(),
            description: it.description || "", quantity: it.quantity ?? 1,
            unit_price: it.unit_price ?? 0, kind: it.kind || "part",
          })),
        });
      }).catch(() => toast.error("Could not load document")).finally(() => setLoading(false));
    }
  }, [editing, id]);

  const pickCustomer = (cid) => {
    const c = customers.find((x) => x.id === cid);
    if (!c) return;
    setForm((f) => ({
      ...f,
      customer_name: c.name || "", customer_phone: c.phone || "", customer_email: c.email || "",
      customer_business_name: c.business_name || "", customer_business_address: c.business_address || "",
      vehicle_make: c.vehicle_make || "", vehicle_model: c.vehicle_model || "",
      vehicle_year: c.vehicle_year || "", vehicle_vin: c.vehicle_vin || "",
    }));
    toast.success(`Loaded ${c.name}`);
  };

  const updateItem = (idx, key, value) =>
    setForm((f) => ({ ...f, items: f.items.map((it, i) => (i === idx ? { ...it, [key]: value } : it)) }));
  const addItem = (kind) => setForm((f) => ({ ...f, items: [...f.items, emptyItem(kind)] }));
  const removeItem = (idx) => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  const addFromCatalog = (jobId) => {
    const job = catalog.find((j) => j.id === jobId);
    if (!job) return;
    const rate = user?.labor_rate || 100;
    const newItems = [];
    if (job.hours) newItems.push({ _key: uid(), description: `${job.name} (labor)`, quantity: Number(job.hours), unit_price: rate, kind: "labor" });
    (job.parts || []).forEach((p) => newItems.push({ _key: uid(), description: p.name, quantity: 1, unit_price: Number(p.price || 0), kind: "part" }));
    setForm((f) => ({ ...f, items: [...f.items.filter((it) => it.description.trim()), ...newItems] }));
    toast.success(`Added "${job.name}" from catalog`);
  };

  const totals = useMemo(() => {
    const subtotal = form.items.reduce((s, it) => s + Number(it.quantity || 0) * Number(it.unit_price || 0), 0);
    const tax = subtotal * (Number(form.tax_rate || 0) / 100);
    return { subtotal, tax, total: subtotal + tax };
  }, [form.items, form.tax_rate]);

  const save = async () => {
    if (!form.customer_name.trim()) return toast.error("Customer name is required");
    setSaving(true);
    const payload = {
      ...form,
      tax_rate: Number(form.tax_rate || 0),
      items: form.items
        .filter((it) => it.description.trim())
        .map((it) => ({ description: it.description, quantity: Number(it.quantity || 0), unit_price: Number(it.unit_price || 0), kind: it.kind })),
    };
    try {
      if (editing) {
        await api.put(`/invoices/${id}`, payload);
        toast.success("Document updated");
        navigate(`/invoices/${id}`);
      } else {
        const { data } = await api.post("/invoices", payload);
        const mktId = location.state?.prefill?.marketplaceReqId;
        const shopVehicleId = location.state?.prefill?.shopVehicleId;
        if (mktId) {
          try {
            await localApi.post(`/marketplace/requests/${mktId}/estimate`, {
              total: totals.total, doc_type: form.doc_type,
              external_id: data.id, summary: payload.items[0]?.description || "",
            });
            toast.success("Document created — the client has been notified");
          } catch {
            toast.success("Document created");
          }
        } else if (shopVehicleId) {
          try {
            await localApi.post(`/shop/vehicles/${shopVehicleId}/documents`, {
              invoice_id: data.id, doc_type: form.doc_type,
              number: data.number || data.invoice_number || "", total: totals.total, status: form.status,
            });
            toast.success("Document created & filed under the vehicle");
          } catch {
            toast.warning("Document created, but it couldn't be filed under the vehicle. You can re-link it from the customer's garage.");
          }
        } else {
          toast.success("Document created");
        }
        navigate(`/invoices/${data.id}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Layout title="Loading…"><div className="flex justify-center py-32"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div></Layout>;
  }

  const field = "h-10 bg-slate-950 border-slate-700 focus-visible:ring-amber-500/50";

  return (
    <Layout
      title={editing ? "Edit Document" : "New Document"}
      action={
        <div className="flex gap-2">
          <Button variant="outline" data-testid="cancel-editor" onClick={() => navigate(-1)}
            className="bg-slate-900 border-slate-700 text-slate-300 hover:bg-slate-800">
            <ArrowLeft className="h-4 w-4 mr-1.5" /> Back
          </Button>
          <Button data-testid="save-invoice-button" onClick={save} disabled={saving}
            className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold">
            {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} Save
          </Button>
        </div>
      }
    >
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 animate-fade-up">
        <div className="xl:col-span-2 space-y-6">
          {/* Doc type + customer */}
          <section className="rounded-xl border border-slate-800 bg-slate-900 p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-head font-bold text-slate-100">Customer</h3>
              <div className="flex items-center gap-2">
                <Label className="text-xs text-slate-500">Type</Label>
                <Select value={form.doc_type} onValueChange={(v) => set("doc_type", v)}>
                  <SelectTrigger data-testid="doc-type-select" className="h-9 w-32 bg-slate-950 border-slate-700"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800 text-slate-200">
                    <SelectItem value="invoice">Invoice</SelectItem>
                    <SelectItem value="estimate">Estimate</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {customers.length > 0 && (
              <div className="mb-4">
                <Label className="text-slate-300 text-xs">Quick-fill from saved customer</Label>
                <Select onValueChange={pickCustomer}>
                  <SelectTrigger data-testid="customer-picker" className="h-10 bg-slate-950 border-slate-700 mt-1.5"><SelectValue placeholder="Select a customer…" /></SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800 text-slate-200 max-h-64">
                    {customers.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}{c.phone ? ` · ${c.phone}` : ""}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-slate-300 text-xs">Name *</Label>
                <Input data-testid="inv-customer-name" value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} className={field} /></div>
              <div className="space-y-1.5"><Label className="text-slate-300 text-xs">Phone</Label>
                <Input value={form.customer_phone} onChange={(e) => set("customer_phone", e.target.value)} className={field} /></div>
              <div className="space-y-1.5"><Label className="text-slate-300 text-xs">Email</Label>
                <Input value={form.customer_email} onChange={(e) => set("customer_email", e.target.value)} className={field} /></div>
              <div className="space-y-1.5"><Label className="text-slate-300 text-xs">Business name</Label>
                <Input value={form.customer_business_name} onChange={(e) => set("customer_business_name", e.target.value)} className={field} /></div>
              <div className="space-y-1.5 sm:col-span-2"><Label className="text-slate-300 text-xs">Address</Label>
                <Input value={form.customer_business_address} onChange={(e) => set("customer_business_address", e.target.value)} className={field} /></div>
            </div>
          </section>

          {/* Vehicle */}
          <section className="rounded-xl border border-slate-800 bg-slate-900 p-5">
            <h3 className="font-head font-bold text-slate-100 mb-4">Vehicle</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="space-y-1.5"><Label className="text-slate-300 text-xs">Year</Label>
                <Input data-testid="inv-vehicle-year" value={form.vehicle_year} onChange={(e) => set("vehicle_year", e.target.value)} className={field} /></div>
              <div className="space-y-1.5"><Label className="text-slate-300 text-xs">Make</Label>
                <Input data-testid="inv-vehicle-make" value={form.vehicle_make} onChange={(e) => set("vehicle_make", e.target.value)} className={field} /></div>
              <div className="space-y-1.5"><Label className="text-slate-300 text-xs">Model</Label>
                <Input data-testid="inv-vehicle-model" value={form.vehicle_model} onChange={(e) => set("vehicle_model", e.target.value)} className={field} /></div>
              <div className="space-y-1.5"><Label className="text-slate-300 text-xs">VIN</Label>
                <Input data-testid="inv-vehicle-vin" value={form.vehicle_vin} onChange={(e) => set("vehicle_vin", e.target.value)} className={field} /></div>
            </div>
          </section>

          {/* Line items */}
          <section className="rounded-xl border border-slate-800 bg-slate-900 p-5">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <h3 className="font-head font-bold text-slate-100">Line Items</h3>
              <div className="flex gap-2 items-center">
                {catalog.length > 0 && (
                  <Select value="" onValueChange={addFromCatalog}>
                    <SelectTrigger data-testid="catalog-picker" className="h-9 w-44 bg-slate-950 border-slate-700 text-slate-300">
                      <span className="flex items-center gap-1.5 text-sm"><BookOpen className="h-3.5 w-3.5" /> From catalog</span>
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-800 text-slate-200 max-h-72">
                      {catalog.map((j) => (<SelectItem key={j.id} value={j.id}>{j.name}{j.system ? ` · ${j.system}` : ""}</SelectItem>))}
                    </SelectContent>
                  </Select>
                )}
                <Button size="sm" variant="outline" data-testid="add-part-item" onClick={() => addItem("part")}
                  className="bg-slate-950 border-slate-700 text-slate-300 hover:bg-slate-800"><Package className="h-3.5 w-3.5 mr-1" /> Part</Button>
                <Button size="sm" variant="outline" data-testid="add-labor-item" onClick={() => addItem("labor")}
                  className="bg-slate-950 border-slate-700 text-slate-300 hover:bg-slate-800"><Wrench className="h-3.5 w-3.5 mr-1" /> Labor</Button>
              </div>
            </div>
            <div className="space-y-2">
              <div className="hidden sm:grid grid-cols-12 gap-2 px-1 text-[11px] uppercase tracking-wide text-slate-500 font-semibold">
                <div className="col-span-5">Description</div><div className="col-span-2">Type</div>
                <div className="col-span-1 text-right">Qty</div><div className="col-span-2 text-right">Unit Price</div>
                <div className="col-span-2 text-right">Amount</div>
              </div>
              {form.items.map((it, idx) => (
                <div key={it._key} data-testid={`line-item-${idx}`} className="grid grid-cols-12 gap-2 items-center">
                  <Input data-testid={`item-desc-${idx}`} value={it.description} onChange={(e) => updateItem(idx, "description", e.target.value)}
                    placeholder={it.kind === "labor" ? "Labor / service" : "Part name"} className="col-span-12 sm:col-span-5 h-10 bg-slate-950 border-slate-700 focus-visible:ring-amber-500/50" />
                  <Select value={it.kind} onValueChange={(v) => updateItem(idx, "kind", v)}>
                    <SelectTrigger className="col-span-4 sm:col-span-2 h-10 bg-slate-950 border-slate-700"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-800 text-slate-200">
                      <SelectItem value="part">Part</SelectItem><SelectItem value="labor">Labor</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input data-testid={`item-qty-${idx}`} type="number" step="0.1" value={it.quantity} onChange={(e) => updateItem(idx, "quantity", e.target.value)}
                    className="col-span-3 sm:col-span-1 h-10 bg-slate-950 border-slate-700 text-right focus-visible:ring-amber-500/50" />
                  <Input data-testid={`item-price-${idx}`} type="number" step="0.01" value={it.unit_price} onChange={(e) => updateItem(idx, "unit_price", e.target.value)}
                    className="col-span-3 sm:col-span-2 h-10 bg-slate-950 border-slate-700 text-right focus-visible:ring-amber-500/50" />
                  <div className="col-span-2 sm:col-span-2 flex items-center justify-end gap-1">
                    <span className="text-sm font-semibold text-slate-200 tabular-nums flex-1 text-right">
                      {money(Number(it.quantity || 0) * Number(it.unit_price || 0))}
                    </span>
                    <Button variant="ghost" size="icon" data-testid={`remove-item-${idx}`} onClick={() => removeItem(idx)}
                      className="h-8 w-8 text-slate-500 hover:text-rose-400 shrink-0"><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </div>
              ))}
              {form.items.length === 0 && (
                <button data-testid="add-first-item" onClick={() => addItem("part")}
                  className="w-full py-6 border border-dashed border-slate-700 rounded-lg text-slate-500 hover:text-amber-400 hover:border-amber-500/40 transition-colors">
                  <Plus className="h-4 w-4 inline mr-1" /> Add line item
                </button>
              )}
            </div>
          </section>

          <section className="rounded-xl border border-slate-800 bg-slate-900 p-5">
            <Label className="text-slate-300 text-xs">Notes</Label>
            <Textarea data-testid="inv-notes" value={form.notes} onChange={(e) => set("notes", e.target.value)}
              placeholder="Internal notes or message to customer…" className="mt-1.5 bg-slate-950 border-slate-700 focus-visible:ring-amber-500/50" />
          </section>
        </div>

        {/* Summary rail */}
        <div className="xl:col-span-1">
          <div className="rounded-xl border border-slate-800 bg-slate-900 p-5 sticky top-24 space-y-4">
            <h3 className="font-head font-bold text-slate-100">Summary</h3>
            <div className="space-y-2">
              <Label className="text-slate-300 text-xs">Status</Label>
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger data-testid="status-select" className="h-10 bg-slate-950 border-slate-700"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800 text-slate-200">
                  {STATUSES.map((s) => (<SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300 text-xs">Tax rate (%)</Label>
              <Input data-testid="inv-tax-rate" type="number" step="0.01" value={form.tax_rate}
                onChange={(e) => set("tax_rate", e.target.value)} className="h-10 bg-slate-950 border-slate-700 focus-visible:ring-amber-500/50" />
            </div>
            <div className="border-t border-slate-800 pt-4 space-y-2 text-sm">
              <div className="flex justify-between text-slate-400"><span>Subtotal</span><span className="tabular-nums text-slate-200" data-testid="summary-subtotal">{money(totals.subtotal)}</span></div>
              <div className="flex justify-between text-slate-400"><span>Tax ({form.tax_rate || 0}%)</span><span className="tabular-nums text-slate-200" data-testid="summary-tax">{money(totals.tax)}</span></div>
              <div className="flex justify-between items-center pt-2 border-t border-slate-800">
                <span className="font-head font-bold text-slate-100">Total</span>
                <span className="font-head font-black text-2xl text-amber-400 tabular-nums" data-testid="summary-total">{money(totals.total)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
